import {
  collection,
  doc,
  getCountFromServer,
  getDocsFromServer,
  onSnapshot,
  query,
  type Unsubscribe,
  where,
  and,
} from "firebase/firestore";
import capitalize from "lodash/capitalize";
import {
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  Identifier,
} from "react-admin";
import { createNote } from "../api/backoffice.api";
import { getAuthenticatedServerClient, serverAPI } from "../api/server.api.js";
import { authProvider, grants } from "../backoffice.access_control";
import { applyFilter, applyPagination, applySort, escapeHtml, reportError } from "../backoffice.utils";
import { firestore } from "../firebase";
import { AttachmentSchema } from "../model/Attachment";
import { Document } from "../model/Document";
import { SignatureRequestSchema, type SignatureRequest } from "../model/SignatureRequest";
import { t } from "../model/types";
import { LocalizedError } from "../utils/LocalizedError";
import { gcs } from "../utils/storage";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { drivingSchoolNotesProvider, studentNotesProvider } from "./notesProvider";
import { studentsProvider } from "./studentsProvider";
import { threadsProvider } from "./threadsProvider.js";

abstract class AbstractDocumentsProvider extends AbstractDataProvider<Document> {
  private _snapshotPromiseRecordId: Identifier | undefined;
  private _snapshotPromise: Promise<Array<Document>> | undefined;

  constructor(private readonly resource: DocumentsSource) {
    super();
  }

  async getManyReference(
    _: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<Document>> {
    if (this.resource === "studentDocuments" && target !== "studentUid") {
      throw new Error(`Unexpected target: ${JSON.stringify(target)} -- expected: "studentUid"`);
    }
    const documents = await this.fetchIfNeeded(id);
    return applyPagination(applySort(applyFilter(documents, filter), sort), pagination);
  }

  async create(
    _: string,
    {
      data,
    }: CreateParams<{
      recordId: string;
      files: Array<{ rawFile: File }>;
      internal?: boolean;
    }>,
  ): Promise<CreateResult<Document>> {
    const { recordId, files, internal } = data;
    if (!recordId) {
      if (this.resource === "studentDocuments") {
        throw new LocalizedError(`Kein Fahrschüler ausgewählt.`);
      }
      if (this.resource === "threadDocuments") {
        throw new LocalizedError(`Kein Thread ausgewählt.`);
      }
      throw new LocalizedError(`Keine Fahrschule ausgewählt.`);
    }
    if (!files || files.length === 0) {
      throw new LocalizedError("Keine Datei ausgewählt.");
    }
    const emptyFile = files.find((it) => it.rawFile.size === 0);
    if (emptyFile) {
      throw new LocalizedError(`Die Datei ${emptyFile.rawFile.name} ist leer.`);
    }
    const knownIds = new Set((await this.fetchIfNeeded(recordId)).map((document) => document.id));
    // Upload files to Google Cloud Storage ...
    const attachments = await gcs.uploadFiles(files);
    // Create note ...
    const { fullName } = (await authProvider.getIdentity?.()) ?? {};
    let noteBody: string;
    if (attachments.length === 1) {
      if (fullName) {
        noteBody = `${escapeHtml(fullName)} hat das Dokument ${escapeHtml(attachments[0].name)} hinzugefügt.`;
      } else {
        noteBody = `Das Dokument ${escapeHtml(attachments[0].name)} wurde hinzugefügt.`;
      }
    } else {
      if (fullName) {
        noteBody = `${escapeHtml(fullName)} hat folgende Dokumente hinzugefügt:<ul>`;
      } else {
        noteBody = "Folgende Dokumente wurden hinzugefügt:";
      }
      noteBody += "<ul>" + attachments.map((it) => `<li>${escapeHtml(it.name)}</li>`).join("") + "</ul>";
    }
    if (this.resource === "studentDocuments") {
      const attachmentsWithInternal = internal ? attachments.map((a) => ({ ...a, internal: true })) : attachments;
      await createNote({ studentUid: recordId, body: noteBody, attachments: attachmentsWithInternal });
      await studentNotesProvider.fetch(recordId);
    } else if (this.resource === "drivingSchoolDocuments") {
      const client = await getAuthenticatedServerClient();
      const newNote = await client.createNote({
        type: "DRIVING_SCHOOL",
        drivingSchoolId: recordId,
        body: noteBody,
        attachments,
      });
      if (!newNote.attachments || newNote.attachments.length === 0) {
        throw new Error("No attachments returned from server.");
      }
      const attachment = newNote.attachments[0];
      let document: Document | undefined;
      let unsubscribe: Unsubscribe | undefined;
      const documentUpdated = new Promise<void>((resolve, reject) => {
        unsubscribe = onSnapshot(collection(firestore, `driving_schools/${recordId}/notes`), async () => {
          try {
            const documents = await this.fetch(recordId);
            document = documents.find((it) => it.id === attachment.id);
            if (document) {
              resolve();
            }
          } catch (error) {
            reject(error);
          }
        });
      });
      await documentUpdated;
      unsubscribe?.();
    } else if (this.resource === "threadDocuments") {
      const client = await getAuthenticatedServerClient();
      const newPost = await client.createPost({
        threadId: recordId,
        body: noteBody,
        attachments,
      });
      if (!newPost.attachments || newPost.attachments.length === 0) {
        throw new Error("No attachments returned from server.");
      }
      const attachment = newPost.attachments[0];
      let document: Document | undefined;
      let unsubscribe: Unsubscribe | undefined;
      const documentUpdated = new Promise<void>((resolve, reject) => {
        unsubscribe = onSnapshot(doc(firestore, `threads/${recordId}`), async () => {
          try {
            const documents = await this.fetch(recordId);
            document = documents.find((it) => it.id === attachment.id);
            if (document) {
              resolve();
            }
          } catch (error) {
            reject(error);
          }
        });
      });
      await documentUpdated;
      unsubscribe?.();
    } else {
      throw new Error(`Unexpected resource: ${this.resource}`);
    }

    const documents = await this.fetch(recordId);
    const newDocument = documents.find((it) => !knownIds.has(it.id) && it.fileName === attachments[0].name)!;
    return { data: newDocument };
  }

  fetchIfNeeded(recordId: Identifier): Promise<Array<Document>> {
    if (this._snapshotPromiseRecordId === recordId) {
      return this._snapshotPromise!;
    }
    return this.fetch(recordId);
  }

  fetch(recordId: Identifier): Promise<Array<Document>> {
    this._snapshotPromiseRecordId = recordId;
    this._snapshotPromise = this._fetch(recordId);
    return this._snapshotPromise;
  }

  public async getSignatureFolderDocuments(drivingSchoolId: string): Promise<Array<SignatureRequest>> {
    if (this.resource !== "studentDocuments") {
      throw new Error(`Unexpected resource: ${this.resource}`);
    }
    if (!drivingSchoolId) {
      throw new Error("No driving school selected.");
    }
    const snapshot = await getDocsFromServer(
      query(
        collection(firestore, "/signature_requests"),
        where("state", "==", "notSigned"),
        where("drivingSchoolId", "==", drivingSchoolId),
        where("deleted", "==", false),
      ),
    );
    return snapshot.docs.map((document) => SignatureRequestSchema.parse({ ...document.data(), uid: document.id }));
  }

  public async getSignatureFolderDocumentsCount(drivingSchoolId: string): Promise<number> {
    if (this.resource !== "studentDocuments") {
      throw new Error(`Unexpected resource: ${this.resource}`);
    }
    try {
      const snapshot = await getCountFromServer(
        query(
          collection(firestore, "/signature_requests"),
          and(
            where("state", "==", "notSigned"),
            where("drivingSchoolId", "==", drivingSchoolId),
            where("deleted", "==", false),
          ),
        ),
      );
      return snapshot.data().count;
    } catch (error) {
      reportError("getSignatureFolderDocumentsCount failed", error);
      return 0;
    }
  }

  private async _fetch(recordId: Identifier): Promise<Array<Document>> {
    const documents = (
      await Promise.all([
        this.retrieveAttachments(recordId),
        this.retrieveDocuments(recordId),
        this.retrieveSignedDocuments(recordId),
      ])
    ).flat();
    console.info(`Retrieved ${documents.length} document(s) for ${this.resourceName} ${recordId}`);
    return documents;
  }

  private get resourceName() {
    switch (this.resource) {
      case "drivingSchoolDocuments":
        return "DrivingSchool";
      case "studentDocuments":
        return "Student";
      case "threadDocuments":
        return "Thread";
    }
  }

  private async retrieveAttachments(recordId: Identifier): Promise<Array<Document>> {
    if (this.resource === "drivingSchoolDocuments") {
      const notes = await drivingSchoolNotesProvider.fetchIfNeeded(recordId);
      const attachments = notes.flatMap((note) => {
        const attachments = note.attachments ?? [];
        return attachments.map((attachment) => {
          return {
            id: attachment.id,
            path: attachment.path,
            fileName: attachment.name,
            contentType: attachment.mimeType,
            createdAt: attachment.createdAt,
            getDownloadUrl: () => gcs.getDownloadUrl(attachment.path),
            type: "drivingSchoolNoteAttachment" as const,
          };
        });
      });
      return attachments;
    } else if (this.resource === "threadDocuments") {
      const thread = await threadsProvider.fetchIfNeeded(recordId);
      const attachments = thread.sortedPosts.flatMap((post) => {
        const attachments = post.attachments ?? [];
        return attachments.map((attachment) => {
          return {
            id: attachment.id,
            path: attachment.path,
            fileName: attachment.name,
            contentType: attachment.mimeType,
            createdAt: attachment.createdAt,
            getDownloadUrl: () => gcs.getDownloadUrl(attachment.path),
            type: "threadAttachment" as const,
          };
        });
      });
      return attachments;
    } else if (this.resource === "studentDocuments") {
      const path =
        this.resource === "studentDocuments"
          ? `/users/${recordId}/attachments`
          : `/driving_schools/${recordId}/attachments`;
      try {
        const snapshot = await getDocsFromServer(collection(firestore, path));
        const attachments = snapshot.docs.map((doc) => AttachmentSchema.parse(doc.data()));
        return this.filterViewedDocuments(
          attachments.map((attachment) => ({
            id: attachment.id,
            path: attachment.path,
            fileName: attachment.name,
            contentType: attachment.mimeType,
            createdAt: attachment.lastChanged,
            getDownloadUrl: () => gcs.getDownloadUrl(attachment.path),
            type: "studentNoteAttachment" as const,
            deleted: attachment.deleted,
            deletedAt: attachment.deletedAt,
            deletedById: attachment.deletedById,
            internal: attachment.internal,
          })),
        );
      } catch (error) {
        reportError(`retrieveAttachments("${recordId}") failed`, error);
        return [];
      }
    }
    throw new Error(`Unexpected resource: ${this.resource}`);
  }

  private async retrieveDocuments(recordId: Identifier): Promise<Array<Document>> {
    if (this.resource === "drivingSchoolDocuments" || this.resource === "threadDocuments") {
      return [];
    }
    try {
      const { data: student } = await studentsProvider.getOne("students", { id: recordId });
      const files = await gcs.listFiles(`/backend/documents/${student.autovioUserId}/${student.drivingSchoolId}`);
      return this.filterViewedDocuments(
        files
          .filter((it) => it.name.endsWith(".pdf"))
          .map((file) => ({
            id: file.fullPath,
            fileName: file.name,
            contentType: "application/pdf",
            createdAt: t.dateTime().parse(file.timeCreated),
            getDownloadUrl: () => gcs.getDownloadUrl(file),
            type: "upload" as const,
            deleted: file.customMetadata?.deleted === "true",
            deletedAt: file.customMetadata?.deletedAt ? t.dateTime().parse(file.customMetadata.deletedAt) : undefined,
            deletedById: file.customMetadata?.deletedBy,
          })),
      );
    } catch (error) {
      reportError(`retrieveDocuments("${recordId}") failed`, error);
      return [];
    }
  }

  private async retrieveSignedDocuments(studentUid: Identifier): Promise<Array<Document>> {
    if (this.resource === "drivingSchoolDocuments" || this.resource === "threadDocuments") {
      return [];
    }
    try {
      const snapshot = await getDocsFromServer(
        query(collection(firestore, "/signature_requests"), where("studentUid", "==", studentUid)),
      );
      const signatureRequest = snapshot.docs.map((doc) => SignatureRequestSchema.parse({ uid: doc.id, ...doc.data() }));
      return this.filterViewedDocuments(
        signatureRequest.map((it) => ({
          id: it.uid,
          fileName: `${capitalize(it.type)}.pdf`,
          contentType: "application/pdf",
          createdAt: it.instructorSignedAt,
          getDownloadUrl: () => gcs.getDownloadUrl(it.signedDocumentStorageRef || it.previewStorageRef),
          type: "signatureRequest" as const,
          deleted: it.deleted,
          deletedAt: it.deletedAt,
          deletedById: it.deletedById,
          signatureRequestState: it.state,
        })),
      );
    } catch (error) {
      reportError(`retrieveSignedDocuments("${studentUid}") failed`, error);
      return [];
    }
  }

  async delete(
    _: string,
    {
      id,
      meta: { document, studentId, drivingSchoolId },
    }: DeleteParams<{ id: string; meta: { document: Document; studentId?: string } }>,
  ): Promise<DeleteResult<Document>> {
    if (!id) throw new Error("Property id must not be empty.");
    if (this.resource !== "studentDocuments") throw new Error(`Unexpected resource: ${this.resource}`);
    const { type } = document;
    if (type === "studentNoteAttachment" && studentId) {
      await serverAPI.deleteStudentAttachment({ studentId, attachmentId: id });
    } else if (type === "upload" && studentId) {
      const studentAutovioId = (await studentsProvider.getOne("students", { id: studentId })).data.autovioUserId;
      await serverAPI.deleteStudentDocument({
        studentId: studentAutovioId,
        documentName: document.fileName,
        drivingSchoolId,
      });
    } else if (type === "signatureRequest") {
      await serverAPI.deleteSignatureRequest({ requestId: id });
    } else {
      throw new Error(`Unexpected type: ${type}`);
    }
    return { data: document };
  }

  private filterViewedDocuments(documents: Array<Document>): Array<Document> {
    if (grants.has("Note:viewDeleted")) {
      return documents;
    }
    return documents.filter((it) => !it.deleted);
  }
}

class StudentDocumentsProvider extends AbstractDocumentsProvider {}
class DrivingSchoolDocumentsProvider extends AbstractDocumentsProvider {}
class ThreadDocumentsProvider extends AbstractDocumentsProvider {}

export const studentDocumentsProvider = new StudentDocumentsProvider("studentDocuments");
export const drivingSchoolDocumentsProvider = new DrivingSchoolDocumentsProvider("drivingSchoolDocuments");
export const threadDocumentsProvider = new ThreadDocumentsProvider("threadDocuments");
export type DocumentsSource = "studentDocuments" | "drivingSchoolDocuments" | "threadDocuments";
